import { useEffect, useState, useCallback } from "react";
import api, { downloadCsv } from "@/lib/api";
import { dt, inr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Filter } from "lucide-react";
import { toast } from "sonner";

export default function WithdrawReport() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", status: "all" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/reports/withdrawals", { params }).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} }));
  }, [f]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const exp = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    downloadCsv("/reports/withdrawals/export", params, `withdrawals_report_${Date.now()}.csv`).catch(() => toast.error("Export failed"));
  };

  const t = data?.totals || {};
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Withdraw transactions report</h2>
          <p className="text-sm text-muted-foreground">Payout requests across their full lifecycle.</p>
        </div>
        <Button variant="outline" onClick={exp} data-testid="withdraw-export-button"><Download className="mr-1.5 h-4 w-4" /> Excel</Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1"><Label className="text-xs">Date from</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="wd-date-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Date to</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="wd-date-to" /></div>
          <div className="space-y-1"><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="wd-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "pending", "approved", "paid", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3"><Button size="sm" onClick={load} data-testid="wd-apply"><Filter className="mr-1.5 h-4 w-4" /> Apply filters</Button></div>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Withdrawals</p><p className="font-mono text-xl font-bold" data-testid="wd-total-count">{t.count ?? "—"}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Total amount</p><p className="font-mono text-xl font-bold">{inr(t.amount)}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Paid out</p><p className="font-mono text-xl font-bold text-success">{inr(t.paid)}</p></Card>
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Settled</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Processed by</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {data && data.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No withdrawals.</TableCell></TableRow>}
              {data && data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`withdraw-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.settled_at)}</TableCell>
                  <TableCell className="text-sm">{r.business_name || r.user_name}</TableCell>
                  <TableCell className="text-xs capitalize">{r.role}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-medium">{inr(r.amount)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm">{r.processed_by_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
